import React, {
    useState, useEffect
} from 'react';
import {
    Popover,
    Grid,
    Table,
    Typography,
    TableContainer,
    TableRow,
    TableCell,
    TableBody,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {useTranslation} from '../common/components/LocalizationProvider';
import StatusCard from '../common/components/StatusCard';
import {devicesActions} from '../store';
import usePersistedState from '../common/util/usePersistedState';
import EventsDrawer from './EventsDrawer';
import useFeatures from '../common/util/useFeatures';
import {Helmet} from 'react-helmet'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomMenuComponent from "../common/components/CustomMenuComponent";
import {useAdministrator} from "../common/util/permissions";
import PositionValue from "../common/components/PositionValue";
import {useAttributePreference} from "../common/util/preferences";
import {temperatureUnitString} from "../common/util/converter";
// import {keyframes} from "@emotion/react/dist/declarations/types";

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
    },
    sidebar: {
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 3,
        margin: theme.spacing(1.5),
        width: theme.dimensions.drawerWidthDesktop,
        bottom: theme.dimensions.bottomBarHeight,
        transition: 'transform .5s ease',
        backgroundColor: 'white',
        [theme.breakpoints.down('md')]: {
            width: '100%',
            margin: 0,
        },
    },
    sidebarCollapsed: {
        transform: `translateX(-${theme.dimensions.drawerWidthDesktop})`,
        marginLeft: 0,
        [theme.breakpoints.down('md')]: {
            transform: 'translateX(-100vw)',
        },
    },
    toolbarContainer: {
        zIndex: 4,
    },
    toolbar: {
        display: 'flex',
        padding: theme.spacing(0, 1),
        '& > *': {
            margin: theme.spacing(0, 1),
        },
    },
    statusCard: {
        position: 'fixed',
        zIndex: 5,
        [theme.breakpoints.up('md')]: {
            left: `calc(50% + ${theme.dimensions.drawerWidthDesktop} / 2)`,
            bottom: theme.spacing(3),
        },
        [theme.breakpoints.down('md')]: {
            left: '50%',
            bottom: `calc(${theme.spacing(3)} + ${theme.dimensions.bottomBarHeight}px)`,
        },
        transform: 'translateX(-50%)',
    },
    sidebarToggle: {
        position: 'fixed',
        left: theme.spacing(1.5),
        top: theme.spacing(3),
        borderRadius: '0px',
        minWidth: 0,
        [theme.breakpoints.down('md')]: {
            left: 0,
        },
    },
    sidebarToggleText: {
        marginLeft: theme.spacing(1),
        [theme.breakpoints.only('xs')]: {
            display: 'none',
        },
    },
    sidebarToggleBg: {
        backgroundColor: 'white',
        color: 'rgba(0, 0, 0, 0.6)',
        '&:hover': {
            backgroundColor: 'white',
        },
    },
    bottomMenu: {
        position: 'fixed',
        left: theme.spacing(1.5),
        bottom: theme.spacing(1.5),
        zIndex: 4,
        width: theme.dimensions.drawerWidthDesktop,
    },
    filterPanel: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2),
        gap: theme.spacing(2),
        width: theme.dimensions.drawerWidthTablet,
    },

    desktopDrawerPaper: {
        width: "100%",
        height: "80px",
        flexDirection: 'row',
        backgroundColor: "grey",
        color: "#fafafa"
    },
    tableStyle: {
        fontSize: "0.8rem"
    },
    normal: {
        backgroundColor: "lightgreen",
        color: "#000000"
    },
    warning: {
        backgroundColor: "#ffea00",
        color: "#000000"
    },
    outdated: {
        backgroundColor: "lightgrey",
        color: "#000000",
        opacity: "0.7"
    },
    lineRowNoPosition: {
        backgroundColor: "lightgrey",
        color: "#000000",
    },
    normalValue: {
        color: "green"
    },
    warningHighValue: {
        color: "#cc3737"
    },
    warningLowValue: {
        color: "#e3982b"
    },
    outdatedValue: {
        color: "#000000",
        // opacity: "0.7"
    },
    formatDivTitle: {
        opacity: 1,
        transition: "500ms",
    },
    formatDivTitleFade: {
        opacity: 0.6,
        transition: "1s",
    },
    blink: {
        animation: `$blinker .75s linear`,
        transitionDuration: "1s",
    },
    "@keyframes blinker": {
        "10%": {
            opacity: 0,
        }
    }
}));

const MainPage = () => {

    // io11	Current value
    // io12	Min 1 hour
    // io13	Max 1 hour
    // io14	Average 1 hour
    // io15	Min 24 hours
    // io16	Max 24 hours
    // io17	Average 24 hours
    // io18	Line power supply

    // Препоръчителни:
    // - Хоризонтален скрол в менюто при по-ниски резолюции
    // - При изтриване на обект от Настройки/Обекти, той не се премахва автоматично от изглед Мониторинг.
    //      При презареждане, изтритият обакт изчезва.
    // - Липсва информация за състоянието на линиите при затворен акордеон Захранване.
    //      Да се добави цветна индикация (1 бр. за всички обекти), която е зелена, ако всички линии са ОК и
    //      жълта/червена, ако някоя от или всички линии са неактивни.
    // - Неуместни опции в различни менюта - показват се неща, несвързани със системата като Ignition start/stop и др.
    //      Да останат само необходимите за работата на системата опции.

    const classes = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const t = useTranslation();
    const admin = useAdministrator();

    const desktop = useMediaQuery(theme.breakpoints.up('xl'));
    const phone = useMediaQuery(theme.breakpoints.down('md'));

    const features = useFeatures();

    const [mapOnSelect] = usePersistedState('mapOnSelect', false);

    const selectedDeviceId = useSelector((state) => state.devices.selectedId);
    const positions = useSelector((state) => state.positions.items);
    const [filteredPositions, setFilteredPositions] = useState([]);

    const groups = useSelector((state) => state.groups.items);
    const devices = useSelector((state) => state.devices.items);
    const [filteredDevices, setFilteredDevices] = useState([]);

    const [filterKeyword, setFilterKeyword] = useState('');
    const [filterStatuses, setFilterStatuses] = useState([]);
    const [filterGroups, setFilterGroups] = useState([]);
    const [filterSort, setFilterSort] = usePersistedState('filterSort', '');
    const [filterMap, setFilterMap] = usePersistedState('filterMap', false);

    const [devicesOpen, setDevicesOpen] = useState(false);
    const [eventsOpen, setEventsOpen] = useState(false);


    useEffect(() => setDevicesOpen(desktop), [desktop]);

    useEffect(() => {
        if (!desktop && mapOnSelect && selectedDeviceId) {
            setDevicesOpen(false);
        }
    }, [desktop, mapOnSelect, selectedDeviceId]);

    const deviceGroups = (device) => {
        const groupIds = [];
        let {groupId} = device;
        while (groupId) {
            groupIds.push(groupId);
            groupId = groups[groupId].groupId;
        }
        return groupIds;
    };

    useEffect(() => {
        const filtered = Object.values(devices)
            .filter((device) => !filterStatuses.length || filterStatuses.includes(device.status))
            .filter((device) => !filterGroups.length || deviceGroups(device).some((id) => filterGroups.includes(id)))
            .filter((device) => {
                const keyword = filterKeyword.toLowerCase();
                return [device.name, device.uniqueId, device.phone, device.model, device.contact].some((s) => s && s.toLowerCase().includes(keyword));
            });
        switch (filterSort) {
            case 'name':
                filtered.sort((device1, device2) => device1.name.localeCompare(device2.name));
                break;
            case 'lastUpdate':
                filtered.sort((device1, device2) => {
                    const time1 = device1.lastUpdate ? moment(device1.lastUpdate).valueOf() : 0;
                    const time2 = device2.lastUpdate ? moment(device2.lastUpdate).valueOf() : 0;
                    return time2 - time1;
                });
                break;
            default:
                break;
        }
        if (filterSort === 'lastUpdate') {
            filtered.sort((device1, device2) => {
                const time1 = device1.lastUpdate ? moment(device1.lastUpdate).valueOf() : 0;
                const time2 = device2.lastUpdate ? moment(device2.lastUpdate).valueOf() : 0;
                return time2 - time1;
            });
        }
        setFilteredDevices(filtered);
        setFilteredPositions(filterMap
            ? filtered.map((device) => positions[device.id]).filter(Boolean)
            : Object.values(positions));
    }, [devices, positions, filterKeyword, filterStatuses, filterGroups, filterSort, filterMap]);

    const sortedFilteredDevices = [...filteredDevices].sort((a, b) =>
        a.name > b.name ? 1 : -1,
    );

    const formatDate = (dateToFormat) => {
        var dateFormatted = moment(dateToFormat).format('YYYY/MM/DD hh:mm:ss A')
        return dateFormatted.toString()
    }

    function checkPositionOutdated(tm) {
        var tmNow = new Date();
        tmNow = new Date(tmNow.getTime() - 10 * 60 * 1000)
        var g2 = new Date(tm);

        if (tmNow.getTime() > g2.getTime()) {
            return classes.outdated
        }
    }

    function checkValueInNormHead(value, reflow, refhigh, tm) {
        var tmNow = new Date();
        tmNow = new Date(tmNow.getTime() - 10 * 60 * 1000)
        var g2 = new Date(tm);
        if (value > refhigh) {
            // if value is higher than refhigh -> red
            return classes.warningHighValue
        } else if (value < reflow) {
            // if value is lower than reflow -> orange
            return classes.warningLowValue
        } else if (tmNow.getTime() > g2.getTime()) {
            return classes.outdatedValue
        } else {
            return classes.normalValue
        }
    }

    function findSelectedDevicePositionId(selectedId) {
        var positionID = null
        Object.entries(positions).map(([positionKey, positionContents], ind) => {
            if (positionKey.toString() === selectedId.toString()) {
                positionID = positionContents
            }
        })
        return positionID
    }

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event, id) => {
        dispatch(devicesActions.select(id))
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;


    function handleTransitionEnd(event) {
        event.target.classList.remove(classes.formatDivTitleFade);
        event.target.classList.add(classes.blink);
    }


    function findArrayElementById(id) {
        return filteredPositions.find((position) => {
            return position.deviceId === id;
        })
    }

    const temperatureUnit = useAttributePreference('temperatureUnit');

    return (
        <>
            <Helmet>
                <title>{t('monitoring')}</title>
            </Helmet>
            <CustomMenuComponent breadcrumbs={['monitoring']}>
                <div className={classes.root}>
                    {sortedFilteredDevices.length > 0 && filteredPositions.length >= 0 &&
                    <>
                        <Grid container p={0.5} spacing={0} columns={{xs: 1, sm: 4, md: 6, lg: 10, xl: 12}}
                              style={desktop ? {
                                  width: "90vw",
                                  margin: "0.5em auto 1em",
                                  alignContent: "left"
                              } : {
                                  margin: "0 auto 0.5em",
                                  alignContent: "left"
                              }}>
                            <Grid item xs={1} sm={2} md={2} lg={2} xl={2} p={0.5}>
                                <Accordion defaultExpanded={true} disableGutters elevation={2}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon/>}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                    >
                                        <Typography>{t('positionPower')}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails style={{padding: 0}}>
                                        {sortedFilteredDevices.map((device, index) => (
                                            device.attributes.isLine === true ?
                                                <div key={index}
                                                     onClick={() => dispatch(devicesActions.select(device.id))}
                                                >
                                                    <TableContainer style={{
                                                        backgroundColor: "transparent"
                                                    }}
                                                                    onClick={handleClick}>
                                                        <Table style={{backgroundColor: "lightgreen"}} size="small">
                                                            <TableBody>
                                                                <TableRow className={findArrayElementById(device.id) ?
                                                                    findArrayElementById(device.id).attributes.io18 === "True" ? classes.normal : classes.warning
                                                                    : classes.lineRowNoPosition}>
                                                                    <TableCell>
                                                                        {device.name.toString()}:
                                                                    </TableCell>
                                                                    <TableCell style={{textAlign: "right"}}>
                                                                        {findArrayElementById(device.id) ?
                                                                            findArrayElementById(device.id).attributes.io18 === "True" ? t('linePowerYes') : t('linePowerNo')
                                                                            : "NA"}
                                                                    </TableCell>
                                                                </TableRow>
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </div>
                                                : null
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        </Grid>
                        <Grid container p={0.5} spacing={0} columns={{xs: 1, sm: 4, md: 6, lg: 10, xl: 12}}
                              style={desktop ? {width: "90vw", margin: "0.5em auto 2em"} : {margin: "0.5em auto 2em"}}>
                            {sortedFilteredDevices.map((device, index) => (
                                device.attributes.isFreezer === true ?
                                    <Grid item xs={1} sm={2} md={2} lg={2} xl={2} key={index} p={0.5}>
                                        <Accordion defaultExpanded={!phone} disableGutters elevation={2}>
                                            <AccordionSummary
                                                className={findArrayElementById(device.id) ? checkPositionOutdated(findArrayElementById(device.id).fixTime) : classes.outdated}
                                                expandIcon={<ExpandMoreIcon/>}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header"
                                            >
                                                <div style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "space-between"
                                                }}
                                                >
                                                    <Typography component="div"
                                                                style={{fontSize: "1rem", float: "left"}}>
                                                        {device.name.toString()}
                                                    </Typography>
                                                    {findArrayElementById(device.id) ?
                                                        <div
                                                            className={checkValueInNormHead(findArrayElementById(device.id).attributes.tempCalculated ?
                                                                    findArrayElementById(device.id).attributes.tempCalculated
                                                                    :
                                                                    findArrayElementById(device.id).attributes.io11,
                                                                device.attributes.lowTemp,
                                                                device.attributes.hiTemp,
                                                                findArrayElementById(device.id).serverTime)
                                                            + ' ' + classes.formatDivTitleFade
                                                            }
                                                            onTransitionEnd={(event) => handleTransitionEnd(event)}
                                                        >
                                                            <PositionValue
                                                                position={findArrayElementById(device.id)}
                                                                property={findArrayElementById(device.id).hasOwnProperty('tempCalculated') ? findArrayElementById(device.id).hasOwnProperty('tempCalculated') : null}
                                                                attribute={findArrayElementById(device.id).attributes ? findArrayElementById(device.id).attributes.hasOwnProperty('tempCalculated') ? "tempCalculated" : null : "NA"}
                                                                showUnit={false}
                                                            />
                                                            <span
                                                                style={{color: "black"}}> {temperatureUnitString(temperatureUnit, t)} </span>
                                                        </div>
                                                        : "NA"
                                                    }
                                                </div>
                                            </AccordionSummary>
                                            <AccordionDetails style={{padding: 0}}>
                                                <TableContainer
                                                    onClick={(e) => handleClick(e, device.id)}>
                                                    <Table style={{backgroundColor: "lightgreen"}} size="small">
                                                        <TableBody>


                                                            {findArrayElementById(device.id) ?
                                                                <TableRow>
                                                                    <TableCell colSpan="4" className={classes.tableStyle}
                                                                               style={{textAlign: "center"}}>
                                                                        {findArrayElementById(device.id).fixTime ? formatDate(findArrayElementById(device.id).fixTime) : "NA"}
                                                                    </TableCell>
                                                                </TableRow>
                                                                :
                                                                null
                                                            }



                                                            {findArrayElementById(device.id) ?
                                                                admin ?
                                                                    <TableRow>
                                                                        <TableCell className={classes.tableStyle}
                                                                                   colSpan="2">
                                                                            {t('currentValue')} {temperatureUnitString(temperatureUnit, t)}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            className={classes.tableStyle}
                                                                            colSpan="1">
                                                                            <div>
                                                                                <PositionValue
                                                                                    position={findArrayElementById(device.id)}
                                                                                    property={findArrayElementById(device.id).hasOwnProperty('io11') ? findArrayElementById(device.id).hasOwnProperty('io11') : null}
                                                                                    attribute={findArrayElementById(device.id).attributes.hasOwnProperty('io11') ? "io11" : null}
                                                                                    showUnit={false}
                                                                                />
                                                                            </div>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                    :
                                                                    null
                                                                :
                                                                null
                                                            }

                                                            <TableRow style={{opacity: "0.6"}}>
                                                                <TableCell className={classes.tableStyle}>#</TableCell>
                                                                <TableCell
                                                                    className={classes.tableStyle}>1 {t('sharedHourAbbreviation')}</TableCell>
                                                                <TableCell
                                                                    className={classes.tableStyle}>24 {t('sharedHourAbbreviation')}</TableCell>
                                                            </TableRow>


                                                            {findArrayElementById(device.id) ?
                                                                <TableRow>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        {t('minimumValue')} {temperatureUnitString(temperatureUnit, t)}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        <div>
                                                                            <PositionValue
                                                                                position={findArrayElementById(device.id)}
                                                                                property={findArrayElementById(device.id).hasOwnProperty('io12') ? findArrayElementById(device.id).hasOwnProperty('io12') : null}
                                                                                attribute={findArrayElementById(device.id).attributes.hasOwnProperty('io12') ? "io12" : null}
                                                                                showUnit={false}
                                                                            />
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        <div>
                                                                            <PositionValue
                                                                                position={findArrayElementById(device.id)}
                                                                                property={findArrayElementById(device.id).hasOwnProperty('io15') ? findArrayElementById(device.id).hasOwnProperty('io15') : null}
                                                                                attribute={findArrayElementById(device.id).attributes.hasOwnProperty('io15') ? "io15" : null}
                                                                                showUnit={false}
                                                                            />
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                                :
                                                                null
                                                            }


                                                            {findArrayElementById(device.id) ?
                                                                <TableRow>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        {t('maximumValue')} {temperatureUnitString(temperatureUnit, t)}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        <div>
                                                                            <PositionValue
                                                                                position={findArrayElementById(device.id)}
                                                                                property={findArrayElementById(device.id).hasOwnProperty('io13') ? findArrayElementById(device.id).hasOwnProperty('io13') : null}
                                                                                attribute={findArrayElementById(device.id).attributes.hasOwnProperty('io13') ? "io13" : null}
                                                                                showUnit={false}
                                                                            />
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        <div>
                                                                            <PositionValue
                                                                                position={findArrayElementById(device.id)}
                                                                                property={findArrayElementById(device.id).hasOwnProperty('io16') ? findArrayElementById(device.id).hasOwnProperty('io16') : null}
                                                                                attribute={findArrayElementById(device.id).attributes.hasOwnProperty('io16') ? "io16" : null}
                                                                                showUnit={false}
                                                                            />
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                                :
                                                                null
                                                            }


                                                            {findArrayElementById(device.id) ?
                                                                <TableRow>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        {t('averageValue')} {temperatureUnitString(temperatureUnit, t)}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        <div>
                                                                            <PositionValue
                                                                                position={findArrayElementById(device.id)}
                                                                                property={findArrayElementById(device.id).hasOwnProperty('io14') ? findArrayElementById(device.id).hasOwnProperty('io14') : null}
                                                                                attribute={findArrayElementById(device.id).attributes.hasOwnProperty('io14') ? "io14" : null}
                                                                                showUnit={false}
                                                                            />
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={classes.tableStyle}>
                                                                        <div>
                                                                            <PositionValue
                                                                                position={findArrayElementById(device.id)}
                                                                                property={findArrayElementById(device.id).hasOwnProperty('io17') ? findArrayElementById(device.id).hasOwnProperty('io17') : null}
                                                                                attribute={findArrayElementById(device.id).attributes.hasOwnProperty('io17') ? "io17" : null}
                                                                                showUnit={false}
                                                                            />
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                                :
                                                                null
                                                            }

                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                    : null
                            ))}
                        </Grid>
                    </>
                    }
                    {!features.disableEvents && <EventsDrawer open={eventsOpen} onClose={() => setEventsOpen(false)}/>}
                    {selectedDeviceId && (
                        <Popover
                            elevation={1}
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClosePopover}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                        >
                            <StatusCard
                                deviceId={selectedDeviceId}
                                onClose={() => dispatch(devicesActions.select(null))}
                                position={findSelectedDevicePositionId(selectedDeviceId)}
                            />
                        </Popover>
                    )}
                </div>
            </CustomMenuComponent>
        </>
    );
};

export default MainPage;
